import { Node } from "@tiptap/core";
import Placeholder from "@tiptap/extension-placeholder";
import type { ParseOptions } from "@tiptap/pm/model";
import { EditorContent, ReactNodeViewRenderer, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { FC, type ReactNode, useEffect, useState } from "react";
import { BiBold, BiItalic, BiListOl, BiListUl, BiPaperPlane } from "react-icons/bi";
import { FaUnderline } from "react-icons/fa";
import { HiOutlineCheck, HiOutlineXMark, HiPlus } from "react-icons/hi2";
import { isApiError, logError } from "../../utils/error";
import { isEmptyRichTextHtml } from "../../utils/richTextHtml";
import { isNetworkError } from "../../utils/upload-error-messages";
import { cn } from "../lib/utils";
import { GifModal } from "./gif/GifModal";
import { KLIPYTagComponent } from "./KlipyTagComponent";
import { Label } from "./Label";

const PRESERVE_WHITESPACE: ParseOptions = { preserveWhitespace: "full" };

const EDITOR_PROPS = {
	attributes: {
		class: "outline-none focus:outline-none py-[12px] px-[16px] min-h-[100px] text-[14px]",
	},
};

const createExtensions = (placeholder: string | undefined) => [
	StarterKit.configure({
		bold: {
			HTMLAttributes: {
				class: "font-[550]",
			},
		},
		link: false,
		listKeymap: false,
	}),
	Placeholder.configure({
		placeholder: placeholder || "Enter text...",
		emptyEditorClass:
			"before:h-0 before:float-left before:content-[attr(data-placeholder)] before:text-gray-400 before:italic cursor-text",
	}),
	KLIPYTag,
];

// The query client already logs and shows request failures; anything else is a bug.
const reportConfirmError = (error: unknown) => {
	if (!(error instanceof Error)) return;
	if (isApiError(error) || isNetworkError(error) || error.name === "TimeoutError") return;
	logError(error);
};

type ToolbarButtonProps = {
	testId: string;
	title: string;
	active?: boolean;
	disabled: boolean;
	onMouseDown: () => void;
	children: ReactNode;
};

const ToolbarButton = ({
	testId,
	title,
	active,
	disabled,
	onMouseDown,
	children,
}: ToolbarButtonProps) => (
	<button
		data-testid={testId}
		title={title}
		disabled={disabled}
		onClick={(e) => e.stopPropagation()}
		onMouseDown={onMouseDown}
		className={cn(
			"flex items-center justify-center w-[26px] h-[26px] rounded text-gray-700 hover:bg-gray-100 hover:text-primary cursor-pointer disabled:cursor-wait disabled:opacity-50",
			active && "bg-gray-200/80 hover:bg-gray-200/80 text-primary",
		)}
	>
		{children}
	</button>
);

export type RichTextEditorProps = {
	content?: string | null;
	ref?: React.Ref<HTMLDivElement>;
	placeholder?: string;
	collapsedPlaceholder?: string;
	onBlur?: () => void;
	onUpdate?: (content: string) => void;
	/** Keep spaces ProseMirror's default parsing trims and collapses, so loaded text matches what was typed. */
	preserveWhitespace?: boolean;
	/** Return a promise to keep the text until it settles: cleared on resolve, kept on reject. */
	onConfirmClick?: (content: string) => void | Promise<unknown>;
	confirmPending?: boolean;
	onCancelClick?: () => void;
	isComment?: boolean;
};

export const RichTextEditor: FC<RichTextEditorProps> = ({
	content,
	ref,
	placeholder,
	collapsedPlaceholder,
	onBlur,
	onUpdate,
	preserveWhitespace = false,
	onConfirmClick,
	confirmPending = false,
	onCancelClick,
	isComment,
}) => {
	const [expanded, setExpanded] = useState(Boolean(!collapsedPlaceholder));
	const [showGifModal, setShowGifModal] = useState(false);
	const [confirming, setConfirming] = useState(false);
	// useEditor reapplies its options whenever they change, so keep them fixed. Later content goes
	// through setContent below.
	const [initialContent] = useState(content);
	const [extensions] = useState(() => createExtensions(placeholder));
	const parseOptions = preserveWhitespace ? PRESERVE_WHITESPACE : undefined;

	const editor = useEditor(
		{
			editable: true,
			content: initialContent,
			parseOptions,
			shouldRerenderOnTransaction: true,
			extensions,
			editorProps: EDITOR_PROPS,
			onBlur: onBlur,
			onUpdate: ({ editor }) => onUpdate?.(editor.getHTML()),
		},
		[], // Remove content from dependencies to prevent editor recreation
	);

	const busy = confirming || confirmPending;
	useEffect(() => {
		if (editor && !editor.isDestroyed) editor.setEditable(!busy, false);
	}, [editor, busy]);

	useEffect(() => {
		if (!editor) return;
		const html = editor.getHTML();
		if (content === html || (isEmptyRichTextHtml(content) && isEmptyRichTextHtml(html))) return;
		// Content set from outside isn't the user's edit, so undo must not step back through it.
		editor
			.chain()
			.setMeta("addToHistory", false)
			.setContent(content || "", { emitUpdate: false, parseOptions })
			.run();
	}, [editor, content, parseOptions]);

	if (!editor) {
		return null;
	}

	return expanded ? (
		<div tabIndex={1} className={cn(!isComment && "bg-white rounded-md border border-slate-200")}>
			<div className="flex items-center h-[32px] px-[4px] space-x-[3px] border-b border-b-gray-100 justify-between bg-gray-50 rounded-t-md">
				<div className="flex flex-1 items-center space-x-[3px] min-w-fit">
					<ToolbarButton
						testId="bold-button"
						title="Bold"
						active={editor.isActive("bold")}
						disabled={busy}
						onMouseDown={() => editor.chain().focus().toggleBold().run()}
					>
						<BiBold className="text-[15px]" />
					</ToolbarButton>
					<ToolbarButton
						testId="italic-button"
						title="Italic"
						active={editor.isActive("italic")}
						disabled={busy}
						onMouseDown={() => editor.chain().focus().toggleItalic().run()}
					>
						<BiItalic className="text-[14px] mt-[-.75px]" />
					</ToolbarButton>
					<ToolbarButton
						testId="underline-button"
						title="Underline"
						active={editor.isActive("underline")}
						disabled={busy}
						onMouseDown={() => editor.chain().focus().toggleUnderline().run()}
					>
						<FaUnderline className="text-[11.5px] mt-[1px]" />
					</ToolbarButton>
					<ToolbarButton
						testId="bullet-list-button"
						title="Unordered List"
						active={editor.isActive("bulletList")}
						disabled={busy}
						onMouseDown={() => editor.chain().focus().toggleBulletList().run()}
					>
						<BiListUl className="text-[18.5px] mt-[1px] mb-[1px]" />
					</ToolbarButton>
					<ToolbarButton
						testId="ordered-list-button"
						title="Ordered List"
						active={editor.isActive("orderedList")}
						disabled={busy}
						onMouseDown={() => editor.chain().focus().toggleOrderedList().run()}
					>
						<BiListOl className="text-[18.5px] mt-[1px] mb-[1px]" />
					</ToolbarButton>
					<ToolbarButton
						testId="gif-button"
						title="Insert GIF"
						disabled={busy}
						onMouseDown={() => setShowGifModal(true)}
					>
						GIF
					</ToolbarButton>
					<div className="flex items-center justify-center !ml-auto">
						{onConfirmClick && (
							<button
								data-testid="condensed-update-button"
								title="Update"
								className="flex items-center justify-center w-[26px] h-[26px] cursor-pointer hover:bg-gray-100 rounded disabled:cursor-wait disabled:opacity-50"
								disabled={busy}
								onClick={async (e) => {
									e.stopPropagation();
									const html = editor.getHTML();
									if (isEmptyRichTextHtml(html)) {
										return;
									}
									const result = onConfirmClick(html);
									if (!(result instanceof Promise)) {
										editor.commands.clearContent();
										return;
									}
									setConfirming(true);
									try {
										await result;
										// Clear only what was confirmed, in case the content was replaced meanwhile,
										// and keep it out of undo so a sent message can't come back.
										if (!editor.isDestroyed && editor.getHTML() === html) {
											editor.chain().setMeta("addToHistory", false).clearContent().run();
										}
									} catch (error) {
										reportConfirmError(error);
									} finally {
										setConfirming(false);
									}
								}}
							>
								{isComment ? (
									<BiPaperPlane className="text-gray-700" size={15} />
								) : (
									<HiOutlineCheck color="green" size={18} />
								)}
							</button>
						)}
						{onCancelClick && (
							<button
								data-testid="condensed-cancel-button"
								title="Cancel"
								className="flex items-center justify-center w-[26px] h-[26px] cursor-pointer hover:bg-gray-100 rounded"
								onClick={(e) => {
									e.stopPropagation();
									onCancelClick && onCancelClick();
								}}
							>
								<HiOutlineXMark color="red" size={18} />
							</button>
						)}
					</div>
				</div>
			</div>
			<EditorContent
				data-testid="editor"
				editor={editor}
				ref={ref}
				onClick={(e) => e.stopPropagation()}
			/>
			{showGifModal && (
				<GifModal
					closeModal={() => setShowGifModal(false)}
					onGifClick={(id: string, searchTerm: string) => {
						setShowGifModal(false);
						if (busy) return;
						editor
							.chain()
							.focus()
							.insertContent([
								{
									type: "klipyTag",
									attrs: { id, searchTerm },
								},
								{ type: "paragraph" },
							])
							.run();
					}}
				/>
			)}
		</div>
	) : (
		<div
			data-testid="collapsed-editor-placeholder"
			tabIndex={1}
			className="flex text-secondary italic items-center cursor-pointer hover:text-tertiary"
			ref={ref}
		>
			<button data-testid="editor-expand-button" onClick={() => setExpanded(true)}>
				<Label size="small" iconBefore={<HiPlus size={16} />}>
					{collapsedPlaceholder || "Add Content"}
				</Label>
			</button>
		</div>
	);
};

const KLIPYTag = Node.create({
	name: "klipyTag",
	group: "block",
	atom: true,
	selectable: false,
	draggable: false,

	// This sets the shape of what the editor expects this tag to be in
	// https://tiptap.dev/docs/editor/markdown/api/utilities#usage
	addAttributes() {
		return {
			id: {
				default: null,
			},
			searchTerm: {
				default: null,
			},
		};
	},

	parseHTML() {
		return [
			{
				tag: "klipy-tag",
				getAttrs: (element) => {
					if (typeof element === "string") {
						return false;
					}
					return {
						id: element.getAttribute("id"),
						searchTerm: element.getAttribute("searchTerm"),
					};
				},
			},
		];
	},

	renderHTML({ HTMLAttributes }) {
		// Defines what is saved in the database/JSON
		return ["klipy-tag", HTMLAttributes];
	},

	addNodeView() {
		// Links the React component to the editor view
		return ReactNodeViewRenderer(KLIPYTagComponent);
	},
});
